import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../../types/batch";

/**
 * An origin id socket: a string, but tolerant of a number.
 *
 * Ingest columns holding a vendor number are frequently numeric, and requiring
 * `string` there would make the edge unmappable. `preprocess` rather than
 * `z.coerce.string()` (which happily turns `null` into `"null"`) or a
 * `.transform()` union (which degrades the inferred socket type to `any` and
 * breaks edge compatibility) — this keeps the socket typed `string` while
 * accepting a numeric upstream value.
 *
 * The handler ALSO casts (`asOriginId`): step inputs are not parsed through this
 * schema before reaching the handler, so the schema documents the contract and
 * the runtime enforces it.
 */
const originIdSocket = z.preprocess(
  (value) =>
    typeof value === "number" && Number.isFinite(value) ? String(value) : value,
  z.string(),
);

/**
 * Upsert Vendor
 *
 * A COMBO leaf node that upserts a Company (`co_companies`) and a Supplier
 * (`ap_suppliers`) from ONE ingest row, downstream of the "Observe DataSource
 * Link" trigger (same fanout shape as "Upsert Product"). Takes ONE item's fields
 * as single-value input sockets; the trigger's lockstep fanout zips its per-field
 * `array<T>` outputs into one item per row, and this step's `batch` config runs
 * the per-item upserts in chunks inside `executeStepBatch`.
 *
 * GRAIN: ONE COMPANY, MANY SUPPLIERS
 * ----------------------------------
 * Each row resolves to at most one company and one supplier, but the company is
 * keyed independently of the supplier, so many rows can resolve to the SAME
 * company. That is the real shape of the source: a firm (`companyOriginId`) has
 * one payable account per division (`supplierOriginId`). Both sides dedupe on
 * their own origin id — a repeated `companyOriginId` never inserts a second
 * company, it updates the existing one and re-parents nothing.
 *
 * WHY THIS IS A COMBO NODE (and not two chained nodes)
 * ----------------------------------------------------
 * The natural modelling is two nodes — Upsert Company (outputs its generated
 * `companyId`) → Upsert Supplier (consumes it, since `ap_suppliers.company_id`
 * is a NOT NULL FK). That chain CANNOT be batched: workflow fanout batching (v1)
 * is only supported on a LEAF step with no outgoing data edges (enforced by
 * `validateBatchConfigs` in `backend/.../workflows/publishValidation.ts`) — a
 * batched step maps over its window and rejoins a single array output, so a
 * non-leaf batched step would change fanout semantics. A batched Upsert Company
 * feeding a batched Upsert Supplier is rejected at publish.
 *
 * So the company→supplier hand-off is done IN-PROCESS inside a single leaf step
 * (`upsertVendorsFromItems`: upsert companies → take their ids → upsert
 * suppliers) rather than across a workflow socket. Marked `experimental` for
 * this reason; if batching ever supports multi-step fanned regions this can be
 * split back into two nodes.
 */

export const upsertVendorStep = defineStepConfig({
  type: "upsert_vendor",
  name: "Upsert Vendor",
  description:
    "Upserts a company (co_companies) and its supplier (ap_suppliers) from one ingest row. Each side dedupes on its own origin id, so many rows can resolve to one company. A batched leaf for the Observe→Upsert backfill: it folds the company→supplier id hand-off in-process because workflow batching (v1) only supports leaf steps. Fan a trigger's per-field array outputs into its single-value inputs.",
  category: "Data Ingest",
  libraryModule: "Platform",

  experimental: {
    reason:
      "Combo company+supplier upsert that exists only because workflow fanout batching is currently leaf-only (a batched Upsert Company → Upsert Supplier chain can't be published). Part of the in-development Observe→Upsert ingest flow.",
  },

  inputSchema: z.object({
    // ── 1. Ingest provenance ────────────────────────────────────────────────
    // Recorded on co_company_data_links / ap_supplier_data_links so each
    // upserted row can be traced back to the ingest-DB row that produced it.
    dataSourceLink: z.object({
      id: z.string().uuid(),
      dataSourceId: z.string().uuid(),
      schemaName: z.string(),
      tableName: z.string(),
    }),
    metadataId: z.string(),

    // ── 2. Company (co_companies) ───────────────────────────────────────────
    companyOriginId: originIdSocket.describe(
      "Dedupe key for co_companies. Repeats update, never insert. Accepts a number.",
    ),
    companyName: z.string(),
    companyEmail: z.string().nullable().optional(),

    // ── 3. Supplier (ap_suppliers) ──────────────────────────────────────────
    supplierOriginId: originIdSocket.describe(
      "Dedupe key for ap_suppliers. Repeats update, never insert. Accepts a number.",
    ),
    supplierName: z.string(),
    supplierEmail: z.string().nullable().optional(),
    supplierPhone: z.string().nullable().optional(),
    supplierAddress: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Raw address text, stored as-is. Resolving it to a core_addresses row is a later backfill.",
      ),
  }),

  outputSchema: z.object({
    companyId: z
      .string()
      .uuid()
      .nullable()
      .describe("The upserted company's id"),
    supplierId: z
      .string()
      .uuid()
      .nullable()
      .describe("The upserted supplier's id"),
    companyCreated: z.boolean().describe("A Company was created"),
    companyUpdated: z.boolean().describe("An existing Company was updated"),
    supplierCreated: z.boolean().describe("A Supplier was created"),
    supplierUpdated: z.boolean().describe("An existing Supplier was updated"),
    skipped: z
      .boolean()
      .describe(
        "Row skipped — missing origin id / name for the company or the supplier",
      ),
  }),

  // Batch-by-design like Upsert Product: this is a leaf built for the batched
  // Observe→Upsert backfill, so it ships a default `batch` config. `batch.size`
  // doubles as the Observe trigger's initial-backfill window size. Read by the
  // engine via `getBatchConfig(step.config)`, not as a normal config field.
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
